if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          if (this.classList.contains('quick-view-modal') && this.modalContent.contains(target)) {
            setTimeout(() => {
              this.initializeQuickViewMediaModal();
              this.initializeQuickViewGallery();
              this.initializeQuickViewZoom();
            }, 50);
          }
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.productModal) {
          this.productModal.remove();
          this.productModal = null;
        }
        this.variantChangeUnsubscriber?.();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (this.classList.contains('quick-view-modal') && this.dataset.colorScheme) {
              const targetScheme = this.dataset.colorScheme;
              Array.from(productElement.classList)
                .filter((className) => className.startsWith('color-'))
                .forEach((className) => productElement.classList.remove(className));
              productElement.classList.add(`color-${targetScheme}`);
              productElement.classList.add('gradient');

              Array.from(this.modalContent.classList)
                .filter((className) => className.startsWith('color-'))
                .forEach((className) => this.modalContent.classList.remove(className));
              this.modalContent.classList.add(`color-${targetScheme}`);
              this.modalContent.classList.add('gradient');

              const modalBox = this.querySelector('.quick-add-modal__content');
              if (modalBox) {
                Array.from(modalBox.classList)
                  .filter((className) => className.startsWith('color-'))
                  .forEach((className) => modalBox.classList.remove(className));
                modalBox.classList.add(`color-${targetScheme}`);
                modalBox.classList.add('gradient');
              }
            }

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (this.classList.contains('quick-view-modal')) {
              this.initializeQuickViewMediaModal();
              this.initializeQuickViewGallery();
              this.initializeQuickViewZoom();
            }

            if (this.parentElement !== document.body) {
              document.body.appendChild(this);
            }

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);

            const modalBoxEl = this.querySelector('.quick-add-modal__content');
            if (modalBoxEl) modalBoxEl.scrollTop = 0;
            if (this.modalContent) this.modalContent.scrollTop = 0;
            const infoWrapperEl = this.querySelector('.product__info-wrapper');
            if (infoWrapperEl) infoWrapperEl.scrollTop = 0;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      initializeQuickViewGallery() {
        const gallery = this.modalContent.querySelector('media-gallery');
        const slider = gallery?.querySelector('[id^="Slider-Gallery-"]');
        const sliderComponent = slider?.closest('slider-component');
        const slides = slider ? Array.from(slider.querySelectorAll(':scope > .product__media-item')) : [];

        if (!sliderComponent || !slides.length) return;

        sliderComponent.querySelector('.quick-view-gallery-dots')?.remove();
        sliderComponent.querySelector('.quick-view-thumbnails')?.remove();
        sliderComponent.querySelectorAll('.slider-buttons, .thumbnail-slider').forEach((el) => el.remove());

        const thumbnailsContainer = document.createElement('div');
        thumbnailsContainer.className = 'quick-view-thumbnails';
        thumbnailsContainer.setAttribute('role', 'group');
        thumbnailsContainer.setAttribute('aria-label', 'Product image gallery');

        let isInternalSwitch = false;

        const getVisibleSlides = () => {
          const filtered = slides.filter((slide) => !slide.classList.contains('is-hidden-by-color'));
          return filtered.length > 0 ? filtered : slides;
        };

        const showSlide = (targetSlideOrIndex) => {
          const visibleSlides = getVisibleSlides();
          let targetSlide;
          if (typeof targetSlideOrIndex === 'number') {
            const safeIndex = Math.min(visibleSlides.length - 1, Math.max(0, targetSlideOrIndex));
            targetSlide = visibleSlides[safeIndex];
          } else if (targetSlideOrIndex && targetSlideOrIndex.nodeType) {
            targetSlide = targetSlideOrIndex;
          }

          if (!targetSlide) {
            targetSlide = visibleSlides[0] || slides[0];
          }

          isInternalSwitch = true;

          slides.forEach((slide) => {
            const isActive = slide === targetSlide;
            const isHiddenByColor = slide.classList.contains('is-hidden-by-color');
            slide.classList.toggle('is-quick-view-active', isActive);
            slide.classList.toggle('is-active', isActive);
            slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

            if (isActive) {
              slide.style.setProperty('display', 'block', 'important');
              slide.style.setProperty('opacity', '1', 'important');
              slide.style.setProperty('visibility', 'visible', 'important');
              slide.style.setProperty('z-index', '2', 'important');
            } else {
              slide.style.setProperty('display', 'none', 'important');
              slide.style.setProperty('opacity', '0', 'important');
              slide.style.setProperty('visibility', 'hidden', 'important');
              slide.style.setProperty('z-index', '1', 'important');
            }
          });

          thumbnailButtons.forEach((btn, index) => {
            const slide = slides[index];
            const isSlideActive = slide === targetSlide;
            const isSlideHidden = slide.classList.contains('is-hidden-by-color');

            btn.classList.toggle('is-active', isSlideActive);
            btn.setAttribute('aria-current', isSlideActive ? 'true' : 'false');

            if (isSlideHidden) {
              btn.style.display = 'none';
              btn.classList.add('is-hidden-by-color');
            } else {
              btn.style.display = '';
              btn.classList.remove('is-hidden-by-color');
            }
          });

          const activeThumb = thumbnailButtons.find((btn, i) => slides[i] === targetSlide);
          if (activeThumb && thumbnailsContainer) {
            const scrollLeft = activeThumb.offsetLeft - (thumbnailsContainer.clientWidth / 2) + (activeThumb.clientWidth / 2);
            thumbnailsContainer.scrollTo({
              left: Math.max(0, scrollLeft),
              behavior: 'smooth'
            });
          }

          setTimeout(() => {
            isInternalSwitch = false;
          }, 60);
        };

        const thumbnailButtons = slides.map((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'quick-view-thumbnail';

          const slideImg = slide.querySelector('img');
          const imageAlt = slideImg?.alt?.trim() || `Thumbnail ${index + 1}`;
          button.setAttribute('aria-label', imageAlt);

          if (slide.dataset.mediaColor) {
            button.dataset.mediaColor = slide.dataset.mediaColor;
          }

          if (slideImg) {
            const thumbImg = document.createElement('img');
            const imgSrc = slideImg.getAttribute('src') || slideImg.src;
            thumbImg.src = imgSrc;
            thumbImg.alt = imageAlt;
            thumbImg.loading = 'lazy';
            button.appendChild(thumbImg);
          }

          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            showSlide(slide);
          });

          thumbnailsContainer.appendChild(button);
          return button;
        });

        const syncThumbnailsVisibility = (targetSlide = null) => {
          const visibleSlides = getVisibleSlides();
          thumbnailsContainer.classList.toggle('quick-view-thumbnails--scrollable', visibleSlides.length > 5);
          thumbnailsContainer.style.display = visibleSlides.length <= 1 ? 'none' : '';

          if (targetSlide && visibleSlides.includes(targetSlide)) {
            showSlide(targetSlide);
          } else {
            const currentActive = visibleSlides.find((s) => s.classList.contains('is-active'));
            showSlide(currentActive || visibleSlides[0]);
          }
        };

        const slideObserver = new MutationObserver(() => {
          if (isInternalSwitch) return;
          const visibleSlides = getVisibleSlides();
          const activeSlide = visibleSlides.find((slide) => slide.classList.contains('is-active'));
          if (activeSlide) {
            showSlide(activeSlide);
          } else if (visibleSlides.length) {
            showSlide(visibleSlides[0]);
          }
        });

        slides.forEach((slide) => {
          slideObserver.observe(slide, { attributes: true, attributeFilter: ['class', 'style'] });
        });

        gallery.addEventListener('slideChanged', (event) => {
          if (isInternalSwitch) return;
          const currentSlide = event?.detail?.currentElement;
          if (currentSlide) showSlide(currentSlide);
        });

        gallery.addEventListener('media-gallery:color-filtered', (event) => {
          syncThumbnailsVisibility(event.detail?.targetSlide);
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.variantChangeUnsubscriber?.();
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            const currentSec = this.modalContent?.querySelector('product-info')?.dataset?.section;
            const originalSec = this.modalContent?.querySelector('product-info')?.dataset?.originalSection;
            const norm = (id) => (id || '').replace(/^quickadd-/, '');
            if (norm(data.sectionId) === norm(currentSec) || norm(data.sectionId) === norm(originalSec)) {
              setTimeout(() => syncThumbnailsVisibility(), 30);
            }
          });
        }

        let touchStartX = null;
        let touchStartY = null;
        let touchSwiped = false;

        slider.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) return;
            touchStartX = event.touches[0].clientX;
            touchStartY = event.touches[0].clientY;
            touchSwiped = false;
          },
          { passive: true }
        );

        slider.addEventListener(
          'touchmove',
          (event) => {
            if (touchStartX === null || event.touches.length !== 1) return;
            const diffX = event.touches[0].clientX - touchStartX;
            const diffY = event.touches[0].clientY - touchStartY;

            if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
              touchSwiped = true;
              if (event.cancelable) {
                event.preventDefault();
              }
            }
          },
          { passive: false }
        );

        slider.addEventListener(
          'touchend',
          (event) => {
            if (touchStartX === null) return;
            const diffX = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
            const diffY = (event.changedTouches[0]?.clientY ?? touchStartY) - touchStartY;
            touchStartX = null;
            touchStartY = null;

            if (Math.abs(diffX) >= 40 && Math.abs(diffX) > Math.abs(diffY)) {
              const visibleSlides = getVisibleSlides();
              const currentIndex = visibleSlides.findIndex((s) => s.classList.contains('is-quick-view-active'));
              const nextIdx = diffX < 0 ? currentIndex + 1 : currentIndex - 1;
              if (nextIdx >= 0 && nextIdx < visibleSlides.length) {
                showSlide(visibleSlides[nextIdx]);
              }
            }
          },
          { passive: true }
        );

        slider.addEventListener('touchcancel', () => {
          touchStartX = null;
          touchStartY = null;
          touchSwiped = false;
        });

        slider.addEventListener(
          'click',
          (event) => {
            if (touchSwiped) {
              event.preventDefault();
              event.stopPropagation();
              touchSwiped = false;
            }
          },
          true
        );

        let pointerStartX;
        let pointerStartY;
        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') return;
          pointerStartX = event.clientX;
          pointerStartY = event.clientY;
        });
        slider.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'touch') return;
          if (pointerStartX === undefined) return;
          const distanceX = event.clientX - pointerStartX;
          const distanceY = event.clientY - (pointerStartY ?? event.clientY);
          pointerStartX = undefined;
          pointerStartY = undefined;
          if (Math.abs(distanceX) < 40 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
          const visibleSlides = getVisibleSlides();
          const currentIndex = visibleSlides.findIndex((s) => s.classList.contains('is-quick-view-active'));
          const nextIdx = distanceX < 0 ? currentIndex + 1 : currentIndex - 1;
          if (nextIdx >= 0 && nextIdx < visibleSlides.length) {
            showSlide(visibleSlides[nextIdx]);
          }
        });
        slider.addEventListener('pointercancel', () => {
          pointerStartX = undefined;
          pointerStartY = undefined;
        });

        sliderComponent.appendChild(thumbnailsContainer);
        syncThumbnailsVisibility();
      }

      initializeQuickViewZoom() {
        const supportsHover = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
        if (!supportsHover.matches) return;

        const modal = this.querySelector('.quick-add-modal__content');
        const mediaWrapper = this.modalContent.querySelector('.product__media-wrapper');
        const infoWrapper = this.modalContent.querySelector('.product__info-wrapper');
        const imageOpeners = this.modalContent.querySelectorAll(
          '.product__media-item .product__modal-opener--image'
        );
        if (!modal || !mediaWrapper || !infoWrapper || !imageOpeners.length) return;

        const zoomRatio = 2;
        const lens = document.createElement('span');
        lens.className = 'quick-view-zoom-lens';
        lens.setAttribute('aria-hidden', 'true');

        const preview = document.createElement('span');
        preview.className = 'quick-view-zoom-preview';
        preview.setAttribute('aria-hidden', 'true');
        this.modalContent.appendChild(preview);

        let activeOpener;
        let zoomFrame;

        const hideZoom = () => {
          window.cancelAnimationFrame(zoomFrame);
          lens.remove();
          lens.classList.remove('is-visible');
          preview.classList.remove('is-visible');
          activeOpener = undefined;
        };

        const largestImageSource = (image) => {
          const candidates = image.srcset
            ? image.srcset.split(',').map((candidate) => candidate.trim().split(/\s+/)[0])
            : [];
          return candidates[candidates.length - 1] || image.currentSrc || image.src;
        };

        const renderedImageBounds = (image) => {
          const bounds = image.getBoundingClientRect();
          const naturalRatio = image.naturalWidth / image.naturalHeight;
          if (!naturalRatio || !bounds.width || !bounds.height) return bounds;

          const boxRatio = bounds.width / bounds.height;
          if (naturalRatio > boxRatio) {
            const height = bounds.width / naturalRatio;
            return new DOMRect(bounds.left, bounds.top + (bounds.height - height) / 2, bounds.width, height);
          }

          const width = bounds.height * naturalRatio;
          return new DOMRect(bounds.left + (bounds.width - width) / 2, bounds.top, width, bounds.height);
        };

        const moveZoom = (event, opener, image) => {
          window.cancelAnimationFrame(zoomFrame);
          zoomFrame = window.requestAnimationFrame(() => {
            if (!opener.closest('.product__media-item')?.classList.contains('is-quick-view-active')) {
              hideZoom();
              return;
            }

            const imageBounds = renderedImageBounds(image);
            if (
              event.clientX < imageBounds.left ||
              event.clientX > imageBounds.right ||
              event.clientY < imageBounds.top ||
              event.clientY > imageBounds.bottom
            ) {
              hideZoom();
              return;
            }

            const modalBounds = modal.getBoundingClientRect();
            const openerBounds = opener.getBoundingClientRect();
            const infoBounds = infoWrapper.getBoundingClientRect();
            const previewSize = Math.min(520, infoBounds.width, imageBounds.width, imageBounds.height * zoomRatio);
            const lensSize = previewSize / zoomRatio;
            const halfLens = lensSize / 2;
            const relativeX = Math.min(
              imageBounds.width - halfLens,
              Math.max(halfLens, event.clientX - imageBounds.left)
            );
            const relativeY = Math.min(
              imageBounds.height - halfLens,
              Math.max(halfLens, event.clientY - imageBounds.top)
            );

            if (lens.parentElement !== opener) opener.appendChild(lens);
            lens.style.width = `${lensSize}px`;
            lens.style.height = `${lensSize}px`;
            lens.style.left = `${imageBounds.left - openerBounds.left + relativeX - halfLens}px`;
            lens.style.top = `${imageBounds.top - openerBounds.top + relativeY - halfLens}px`;

            preview.style.left = `${infoBounds.left - modalBounds.left}px`;
            preview.style.top = `${imageBounds.top - modalBounds.top}px`;
            preview.style.width = `${previewSize}px`;
            preview.style.height = `${previewSize}px`;
            preview.style.backgroundImage = `url("${largestImageSource(image)}")`;
            preview.style.backgroundSize = `${imageBounds.width * zoomRatio}px ${imageBounds.height * zoomRatio}px`;
            preview.style.backgroundPosition = `${-(relativeX - halfLens) * zoomRatio}px ${
              -(relativeY - halfLens) * zoomRatio
            }px`;

            lens.classList.add('is-visible');
            preview.classList.add('is-visible');
          });
        };

        imageOpeners.forEach((opener) => {
          const image = opener.querySelector('.product__media img');
          if (!image) return;

          opener.addEventListener(
            'click',
            (event) => {
              const imageBounds = renderedImageBounds(image);
              if (
                event.clientX < imageBounds.left ||
                event.clientX > imageBounds.right ||
                event.clientY < imageBounds.top ||
                event.clientY > imageBounds.bottom
              ) return;

              event.preventDefault();
              event.stopPropagation();

              if (activeOpener === opener) {
                hideZoom();
                return;
              }

              activeOpener = opener;
              moveZoom(event, opener, image);
            },
            true
          );
          opener.addEventListener('pointermove', (event) => {
            if (activeOpener !== opener || event.pointerType !== 'mouse' || event.buttons) return;
            moveZoom(event, opener, image);
          });
          opener.addEventListener('pointerleave', hideZoom);
        });

        this.modalContent.querySelector('.quick-view-thumbnails')?.addEventListener('click', hideZoom);
        infoWrapper.addEventListener('pointerenter', hideZoom);
      }

      initializeQuickViewMediaModal() {
        const opener = this.modalContent.querySelector('.product__modal-opener--image');
        const modalSelector = opener?.getAttribute('data-modal');
        const productModal = modalSelector ? document.querySelector(modalSelector) : null;

        if (!productModal) return;

        productModal.classList.add('quick-view-product-media-modal');
        this.productModal = productModal;
      }

      preprocessHTML(productElement) {
        if (!this.classList.contains('quick-view-modal')) {
          productElement.classList.forEach((classApplied) => {
            if (classApplied.startsWith('color-') || classApplied === 'gradient')
              this.modalContent.classList.add(classApplied);
          });
        }
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const breadcrumbs = productElement.querySelectorAll(
          '.product-breadcrumb, nav.product-breadcrumb, [aria-label="Breadcrumb"], [aria-label="breadcrumbs"]'
        );
        if (breadcrumbs) breadcrumbs.forEach((bc) => bc.remove());

        if (this.classList.contains('quick-view-modal')) {
          productElement.querySelectorAll('.slider-buttons, .thumbnail-slider').forEach((el) => el.remove());
        }

        if (!this.classList.contains('quick-view-modal')) {
          const productModal = productElement.querySelector('product-modal');
          if (productModal) productModal.remove();
        }

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId =
          productElement.dataset?.section ||
          productElement.closest('product-info')?.dataset?.section ||
          this.modalContent?.querySelector('product-info')?.dataset?.originalSection;

        if (!sectionId) return;

        const oldId = sectionId.replace(/^quickadd-/, '');
        const newId = `quickadd-${oldId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replaceAll(oldId, newId));
          }
        });

        productElement.dataset.originalSection = oldId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
